import { getWebSocketUrl } from '@/config/runtime-env'
import { WEBSOCKET_EVENTS } from '@/utils/constants'
import { io, Socket } from 'socket.io-client'
import type { BulkPresenceResponse, CounselorPresenceStatus } from './presence'
import type {
  CallAcceptedData,
  CallEndedData,
  CallRejectedData,
  IncomingCallData
} from './video-call'

// Chat-related types
export interface ChatMessage {
  id: string;
  sessionId: string;
  senderId: string;
  message: string;
  messageType: 'text' | 'image' | 'file' | 'system';
  sentAt: string;
  fileId?: string;
  file?: {
    id: string;
    fileName: string;
    fileType: string;
    fileSize: number;
  };
}

export interface SendMessagePayload {
  sessionId: string;
  message?: string;
  messageType?: 'text' | 'file';
  fileId?: string;
}

export type VideoCallEventHandlers = {
  onIncomingCall?: (data: IncomingCallData) => void;
  onCallAccepted?: (data: CallAcceptedData) => void;
  onCallRejected?: (data: CallRejectedData) => void;
  onCallEnded?: (data: CallEndedData) => void;
  onError?: (error: string) => void;
};

export type PresenceEventHandlers = {
  onPresenceStatusChanged?: (data: CounselorPresenceStatus) => void;
  onCounselorWentOnline?: (data: CounselorPresenceStatus) => void;
  onCounselorWentOffline?: (data: CounselorPresenceStatus) => void;
  onCounselorBecameBusy?: (data: CounselorPresenceStatus) => void;
  onCounselorBecameAvailable?: (data: CounselorPresenceStatus) => void;
  onBulkPresenceUpdate?: (data: BulkPresenceResponse) => void;
};

export type ChatEventHandlers = {
  onNewMessage?: (payload: { message: ChatMessage }) => void;
  onNewMessageNotification?: (data: { sessionId: string; message: string; senderId: string }) => void;
  onSessionJoined?: (data: { sessionId: string }) => void;
  onJoinSessionFailed?: (data: { error: string; sessionId?: string }) => void;
  onSessionLeft?: (data: { sessionId: string }) => void;
  onUserTyping?: (data: { sessionId: string; userId: string; isTyping: boolean }) => void;
  onChatError?: (error: { message: string; code: number }) => void;
  onSessionsListUpdated?: (data: { sessionId: string; latestMessage: any; unreadCount: number; timestamp: Date }) => void;
  onUnreadCountUpdated?: (data: { sessionId: string; unreadCount: number; timestamp: Date }) => void;
};

type HandlerMap = {
  [K in keyof VideoCallEventHandlers]: Array<NonNullable<VideoCallEventHandlers[K]>>
} & {
  [K in keyof PresenceEventHandlers]: Array<NonNullable<PresenceEventHandlers[K]>>
} & {
  [K in keyof ChatEventHandlers]: Array<NonNullable<ChatEventHandlers[K]>>
};

class WebSocketService {
  private socket: Socket | null = null;
  private eventHandlers: HandlerMap = {
    onIncomingCall: [],
    onCallAccepted: [],
    onCallRejected: [],
    onCallEnded: [],
    onError: [],
    onPresenceStatusChanged: [],
    onCounselorWentOnline: [],
    onCounselorWentOffline: [],
    onCounselorBecameBusy: [],
    onCounselorBecameAvailable: [],
    onBulkPresenceUpdate: [],
    onNewMessage: [],
    onNewMessageNotification: [],
    onSessionJoined: [],
    onJoinSessionFailed: [],
    onSessionLeft: [],
    onUserTyping: [],
    onChatError: [],
    onSessionsListUpdated: [],
    onUnreadCountUpdated: []
  };
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 5000;

  /**
   * Initialize WebSocket connection
   */
  async connect(token: string): Promise<void> {
    if (this.socket?.connected) {
      return;
    }

    const websocketUrl = getWebSocketUrl();
    if (!websocketUrl) {
      throw new Error('WebSocket URL not configured');
    }

    try {
      this.socket = io(`${websocketUrl}/chat`, {
        auth: { token },
        query: { token },
        extraHeaders: { Authorization: `Bearer ${token}` },
        transports: ['websocket'],
        upgrade: false,
        autoConnect: true,
      });

      this.setupEventListeners();
      this.reconnectAttempts = 0;

      return new Promise((resolve, reject) => {
        this.socket!.on('connect', () => {
          console.log('WebSocket connected successfully');
          resolve();
        });

        this.socket!.on('connect_error', (error) => {
          console.error('WebSocket connection error:', error);
          reject(error);
        });

        // Set timeout for connection
        setTimeout(() => {
          if (!this.socket?.connected) {
            reject(new Error('WebSocket connection timeout'));
          }
        }, 10000);
      });
    } catch (error) {
      console.error('Failed to initialize WebSocket:', error);
      throw error;
    }
  }

  /**
   * Disconnect WebSocket
   */
  disconnect(): void {
    console.log('🔌 Disconnecting WebSocket');
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    // Clear all handlers on disconnect
    this.eventHandlers = {
      onIncomingCall: [],
      onCallAccepted: [],
      onCallRejected: [],
      onCallEnded: [],
      onError: [],
      onPresenceStatusChanged: [],
      onCounselorWentOnline: [],
      onCounselorWentOffline: [],
      onCounselorBecameBusy: [],
      onCounselorBecameAvailable: [],
      onBulkPresenceUpdate: [],
      onNewMessage: [],
      onNewMessageNotification: [],
      onSessionJoined: [],
      onJoinSessionFailed: [],
      onSessionLeft: [],
      onUserTyping: [],
      onChatError: [],
      onSessionsListUpdated: [],
      onUnreadCountUpdated: []
    };
  }

  /**
   * Add video call event handlers (accumulates instead of replacing)
   */
  addVideoCallHandlers(handlers: VideoCallEventHandlers): string {
    const handlerId = Math.random().toString(36).substr(2, 9);
    console.log(`🔧 Adding video call handlers with ID: ${handlerId}`);

    // Add each handler to the respective array
    Object.entries(handlers).forEach(([key, handler]) => {
      if (handler && key in this.eventHandlers) {
        (this.eventHandlers as any)[key].push(handler);
        console.log(`✅ Added ${key} handler`);
      }
    });

    return handlerId;
  }

  /**
   * Add presence event handlers (accumulates instead of replacing)
   */
  addPresenceHandlers(handlers: PresenceEventHandlers): string {
    const handlerId = Math.random().toString(36).substr(2, 9);
    console.log(`🔧 Adding presence handlers with ID: ${handlerId}`);

    // Add each handler to the respective array
    Object.entries(handlers).forEach(([key, handler]) => {
      if (handler && key in this.eventHandlers) {
        (this.eventHandlers as any)[key].push(handler);

      }
    });

    return handlerId;
  }

  /**
   * Add chat event handlers (accumulates instead of replacing)
   */
  addChatHandlers(handlers: ChatEventHandlers): string {
    const handlerId = Math.random().toString(36).substr(2, 9);

    // Add each handler to the respective array
    Object.entries(handlers).forEach(([key, handler]) => {
      if (handler && key in this.eventHandlers) {
        (this.eventHandlers as any)[key].push(handler);
      }
    });

    return handlerId;
  }

  /**
   * Remove video call event handlers by ID
   */
  removeVideoCallHandlers(handlerId: string): void {
    console.log(`🗑️ Removing video call handlers with ID: ${handlerId}`);
    // For now, we'll clear all handlers since we don't track individual handler IDs
    // In a more sophisticated implementation, we'd track handler IDs
    this.eventHandlers = {
      onIncomingCall: [],
      onCallAccepted: [],
      onCallRejected: [],
      onCallEnded: [],
      onError: [],
      onPresenceStatusChanged: [],
      onCounselorWentOnline: [],
      onCounselorWentOffline: [],
      onCounselorBecameBusy: [],
      onCounselorBecameAvailable: [],
      onBulkPresenceUpdate: []
    };
  }

  /**
   * Remove presence event handlers by ID
   */
  removePresenceHandlers(_handlerId: string): void {
    // For now, we'll clear all presence handlers since we don't track individual handler IDs
    this.eventHandlers.onPresenceStatusChanged = [];
    this.eventHandlers.onCounselorWentOnline = [];
    this.eventHandlers.onCounselorWentOffline = [];
    this.eventHandlers.onCounselorBecameBusy = [];
    this.eventHandlers.onCounselorBecameAvailable = [];
    this.eventHandlers.onBulkPresenceUpdate = [];
  }

  /**
   * Remove chat event handlers by ID
   */
  removeChatHandlers(_handlerId: string): void {
    // For now, we'll clear all chat handlers since we don't track individual handler IDs
    this.eventHandlers.onNewMessage = [];
    this.eventHandlers.onNewMessageNotification = [];
    this.eventHandlers.onSessionJoined = [];
    this.eventHandlers.onJoinSessionFailed = [];
    this.eventHandlers.onSessionLeft = [];
    this.eventHandlers.onUserTyping = [];
    this.eventHandlers.onChatError = [];
    this.eventHandlers.onSessionsListUpdated = [];
    this.eventHandlers.onUnreadCountUpdated = [];
  }

  /**
   * Legacy method for backward compatibility (now adds handlers)
   */
  setVideoCallHandlers(handlers: VideoCallEventHandlers): void {
    this.addVideoCallHandlers(handlers);
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Setup event listeners for WebSocket
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      this.reconnectAttempts = 0;
      // Re-register handlers after reconnection
      this.reregisterHandlers();
    });

    this.socket.on('disconnect', (reason) => {
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, don't reconnect
        return;
      }
      this.handleReconnect();
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      this.handleReconnect();
    });

    // Video call events
    this.socket.on('incoming_call', (data: IncomingCallData) => {
      this.eventHandlers.onIncomingCall?.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('Error in incoming call handler:', error);
        }
      });
    });

    this.socket.on('call_accepted', (data: CallAcceptedData) => {
      this.eventHandlers.onCallAccepted?.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('Error in call accepted handler:', error);
        }
      });
    });

    this.socket.on('call_rejected', (data: CallRejectedData) => {
      this.eventHandlers.onCallRejected?.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('Error in call rejected handler:', error);
        }
      });
    });

    this.socket.on('call_ended', (data: CallEndedData) => {
      this.eventHandlers.onCallEnded?.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('Error in call ended handler:', error);
        }
      });
    });

    this.socket.on('error', (error: string) => {
      console.error('WebSocket error:', error);
      this.eventHandlers.onError?.forEach(handler => {
        try {
          handler(error);
        } catch (handlerError) {
          console.error('Error in error handler:', handlerError);
        }
      });
    });

    // Presence events
    this.socket.on(WEBSOCKET_EVENTS.PRESENCE_STATUS_CHANGED, (data: CounselorPresenceStatus) => {
      this.eventHandlers.onPresenceStatusChanged?.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('Error in presence status changed handler:', error);
        }
      });
    });

    this.socket.on(WEBSOCKET_EVENTS.COUNSELOR_WENT_ONLINE, (data: CounselorPresenceStatus) => {
      this.eventHandlers.onCounselorWentOnline?.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('Error in counselor went online handler:', error);
        }
      });
    });

    this.socket.on(WEBSOCKET_EVENTS.COUNSELOR_WENT_OFFLINE, (data: CounselorPresenceStatus) => {
      this.eventHandlers.onCounselorWentOffline?.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('Error in counselor went offline handler:', error);
        }
      });
    });

    this.socket.on(WEBSOCKET_EVENTS.COUNSELOR_BECAME_BUSY, (data: CounselorPresenceStatus) => {
      this.eventHandlers.onCounselorBecameBusy?.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('Error in counselor became busy handler:', error);
        }
      });
    });

    this.socket.on(WEBSOCKET_EVENTS.COUNSELOR_BECAME_AVAILABLE, (data: CounselorPresenceStatus) => {
      this.eventHandlers.onCounselorBecameAvailable?.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('Error in counselor became available handler:', error);
        }
      });
    });

    this.socket.on(WEBSOCKET_EVENTS.BULK_PRESENCE_UPDATE, (data: BulkPresenceResponse) => {
      this.eventHandlers.onBulkPresenceUpdate?.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('Error in bulk presence update handler:', error);
        }
      });
    });

    // Authentication event
    this.socket.on('authenticated', () => {
      // WebSocket authenticated successfully
    });

    // Chat message events
    this.socket.on('new_message', (payload: { sessionId: string; message: ChatMessage; sender: { id: string } }) => {
      console.log('New message received:', payload.message?.message);

      this.eventHandlers.onNewMessage?.forEach((handler) => {
        try {
          // Transform backend payload to expected format for compatibility
          const transformedPayload = { message: payload.message };
          handler(transformedPayload);
        } catch (error) {
          console.error('Error in new message handler:', error);
        }
      });
    });

    this.socket.on('new_message_notification', (data: { sessionId: string; message: string; senderId: string }) => {
      this.eventHandlers.onNewMessageNotification?.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('Error in new message notification handler:', error);
        }
      });
    });

    this.socket.on('session_joined', (data: { sessionId: string }) => {
      this.eventHandlers.onSessionJoined?.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('Error in session joined handler:', error);
        }
      });
    });

    this.socket.on('join_session_failed', (data: { error: string; sessionId?: string }) => {
      this.eventHandlers.onJoinSessionFailed?.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('Error in join session failed handler:', error);
        }
      });
    });

    this.socket.on('session_left', (data: { sessionId: string }) => {
      this.eventHandlers.onSessionLeft?.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('Error in session left handler:', error);
        }
      });
    });

    this.socket.on('user_typing', (data: { sessionId: string; userId: string; isTyping: boolean }) => {
      this.eventHandlers.onUserTyping?.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('Error in user typing handler:', error);
        }
      });
    });

    this.socket.on('error', (error: { message: string; code: number }) => {
      console.error('Chat error:', error);
      this.eventHandlers.onChatError?.forEach(handler => {
        try {
          handler(error);
        } catch (handlerError) {
          console.error('Error in chat error handler:', handlerError);
        }
      });
    });

    // Session list update events
    this.socket.on('sessions_list_updated', (data: { sessionId: string; latestMessage: any; unreadCount: number; timestamp: Date }) => {
      this.eventHandlers.onSessionsListUpdated?.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('Error in sessions list updated handler:', error);
        }
      });
    });

    this.socket.on('unread_count_updated', (data: { sessionId: string; unreadCount: number; timestamp: Date }) => {
      this.eventHandlers.onUnreadCountUpdated?.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('Error in unread count updated handler:', error);
        }
      });
    });
  }

  /**
   * Re-register handlers after reconnection
   */
  private reregisterHandlers(): void {
    // Event handlers are automatically re-bound when socket reconnects
    // due to the setupEventListeners() call in connect()
  }

  /**
   * Handle reconnection logic
   */
  private handleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;

    setTimeout(() => {
      if (!this.socket?.connected) {
        this.socket?.connect();
      }
    }, this.reconnectInterval);
  }

  /**
   * Emit authentication event
   */
  authenticate(token: string): void {
    if (this.socket?.connected) {
      this.socket.emit('authenticate', { token });
    }
  }

  /**
   * Chat-specific operations
   */
  joinSession(sessionId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('join_session', { sessionId });
    } else {
      console.error('WebSocket not connected. Cannot join session.');
    }
  }

  leaveSession(sessionId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('leave_session', { sessionId });
    } else {
      console.error('WebSocket not connected. Cannot leave session.');
    }
  }

  sendMessage(payload: SendMessagePayload): void {
    if (this.socket?.connected) {
      console.log('Sending message:', payload.message);
      this.socket.emit('send_message', payload);
    } else {
      console.error('WebSocket not connected. Cannot send message.');
    }
  }

  startTyping(sessionId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('typing_start', { sessionId });
    }
  }

  stopTyping(sessionId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('typing_stop', { sessionId });
    }
  }

  /**
   * Get connection status for debugging
   */
  getConnectionStatus(): {
    connected: boolean;
    handlerCounts: Record<string, number>;
    reconnectAttempts: number;
  } {
    return {
      connected: this.socket?.connected ?? false,
      handlerCounts: {
        onIncomingCall: this.eventHandlers.onIncomingCall?.length || 0,
        onCallAccepted: this.eventHandlers.onCallAccepted?.length || 0,
        onCallRejected: this.eventHandlers.onCallRejected?.length || 0,
        onCallEnded: this.eventHandlers.onCallEnded?.length || 0,
        onError: this.eventHandlers.onError?.length || 0,
        onPresenceStatusChanged: this.eventHandlers.onPresenceStatusChanged?.length || 0,
        onCounselorWentOnline: this.eventHandlers.onCounselorWentOnline?.length || 0,
        onCounselorWentOffline: this.eventHandlers.onCounselorWentOffline?.length || 0,
        onCounselorBecameBusy: this.eventHandlers.onCounselorBecameBusy?.length || 0,
        onCounselorBecameAvailable: this.eventHandlers.onCounselorBecameAvailable?.length || 0,
        onBulkPresenceUpdate: this.eventHandlers.onBulkPresenceUpdate?.length || 0,
        onNewMessage: this.eventHandlers.onNewMessage?.length || 0,
        onNewMessageNotification: this.eventHandlers.onNewMessageNotification?.length || 0,
        onSessionJoined: this.eventHandlers.onSessionJoined?.length || 0,
        onJoinSessionFailed: this.eventHandlers.onJoinSessionFailed?.length || 0,
        onSessionLeft: this.eventHandlers.onSessionLeft?.length || 0,
        onUserTyping: this.eventHandlers.onUserTyping?.length || 0,
        onChatError: this.eventHandlers.onChatError?.length || 0,
        onSessionsListUpdated: this.eventHandlers.onSessionsListUpdated?.length || 0,
        onUnreadCountUpdated: this.eventHandlers.onUnreadCountUpdated?.length || 0,
      },
      reconnectAttempts: this.reconnectAttempts,
    };
  }
}

// Export singleton instance
export const webSocketService = new WebSocketService();